import { Input } from "@chakra-ui/react";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { usePendingSaves } from "../pendingSaves";

export const convertISOToDate = (date?: string | null) =>
  date && moment(date).isValid() ? moment(date).format("YYYY-MM-DD") : "";
const convertDateToISO = (date: string) => moment(date).toISOString();

export const ISODateSelector = ({
  defaultDate,
  date,
  isDisabled,
  onChange,
  autoFocus,
  onBlur,
  savesTypingOnLeave = false,
  onIncompleteChange,
}: {
  defaultDate?: string;
  date?: string | null;
  onChange: (isoDate?: string) => Promise<void> | void;
  isDisabled?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
  savesTypingOnLeave?: boolean;
  onIncompleteChange?: (isIncomplete: boolean) => void;
}) => {
  const [internalDate, setInternalDate] = useState<string>(convertISOToDate(defaultDate) || "");
  const isTyping = useRef(false);
  const committedDate = useRef(internalDate);
  const replacedDate = convertISOToDate(defaultDate);

  useEffect(() => {
    if (!savesTypingOnLeave) return;
    committedDate.current = replacedDate;
    if (!isTyping.current) setInternalDate(replacedDate);
  }, [replacedDate, savesTypingOnLeave]);

  const commit = (date: string) => {
    isTyping.current = false;
    if (date === committedDate.current) return;
    committedDate.current = date;
    onChange(date ? convertDateToISO(date) : undefined);
  };
  const field = useRef<HTMLInputElement>(null);
  const commitTyped = useRef(() => {});
  commitTyped.current = () => {
    if (isTyping.current && field.current && !field.current.validity.badInput) commit(field.current.value);
  };

  const pendingSaves = usePendingSaves();
  useEffect(() => {
    if (!savesTypingOnLeave || !pendingSaves) return;
    const typing = { flush: () => undefined, commitTyping: () => commitTyped.current() };
    pendingSaves.add(typing);
    return () => {
      pendingSaves.delete(typing);
    };
  }, [pendingSaves, savesTypingOnLeave]);

  const commitOnLeave = (input: HTMLInputElement) => {
    const isIncomplete = input.validity.badInput;
    onIncompleteChange?.(isIncomplete);
    if (!isIncomplete) commit(input.value);
  };

  return (
    <Input
      ref={field}
      fontSize="md"
      type="date"
      isDisabled={isDisabled}
      autoFocus={autoFocus}
      value={convertISOToDate(date !== undefined ? date : internalDate)}
      onKeyDown={(e) => {
        if (!savesTypingOnLeave) return;
        if (e.key === "Enter") commitOnLeave(e.currentTarget);
        else if (e.key === "Tab") commitTyped.current();
        else isTyping.current = true;
      }}
      onPointerDown={() => {
        isTyping.current = false;
      }}
      onChange={(e) => {
        const date = e.target.value;
        setInternalDate(date);
        if (savesTypingOnLeave && !e.target.validity.badInput) onIncompleteChange?.(false);
        if (savesTypingOnLeave && isTyping.current) return;
        commit(date);
      }}
      onBlur={(e) => {
        if (savesTypingOnLeave) commitOnLeave(e.currentTarget);
        onBlur?.();
      }}
      width="60"
      border="1px solid"
      borderColor="smInputBorder"
      backgroundColor="smBackground"
      rounded="md"
      p="4"
    />
  );
};
